import React, { useEffect, useState, useRef } from 'react';
import { Brodcrumb } from '../ui/UtilsLayout';
import Input from '../ui/Input';
import { iconsPath, imagePath } from '../../utils/imagePath';
import { Drawer } from '../ui/Drawer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { helpCenterUseCases } from '../data/data/HelpCenter/HelpCenterData';

// Helper function to generate consistent IDs
export const generateSectionId = (title) => {
  if (!title || typeof title !== 'string') return '';
  return title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
};

const ArticleLayout = ({
  articleTitles,
  tocData,
  ContentBodyComponent,
  sectionName,
  searchPlaceholder = 'Search the Spatial Grid Help Center',
  showUseCases = true,
  showTitle = true,
}) => {
  const [open, setOpen] = useState(false);
  const [openToc, setOpenToc] = useState(false);
  const [activeId, setActiveId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const articleIndex = Number(searchParams.get('article')) || 0;
  const [activeLeft, setActiveLeft] = useState(articleIndex);

  const observerRef = useRef(null);
  const searchRef = useRef(null);
  const tocRef = useRef(null);
  const contentScrollRef = useRef(null); // NEW: Reference to scrollable content area

  useEffect(() => {
    setActiveLeft(articleIndex);
  }, [articleIndex]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
      if (tocRef.current && !tocRef.current.contains(event.target)) {
        setOpenToc(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll spy effect
  useEffect(() => {
    // CHANGED: Scroll only the content container to top
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }

    const topics = tocData[activeLeft] || tocData[0] || [];
    const sectionIds = topics.map(generateSectionId);

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Fallback scroll handler for manual checking
    const handleScroll = () => {
      if (!contentScrollRef.current) return;

      const scrollTop = contentScrollRef.current.scrollTop;
      const containerTop = contentScrollRef.current.getBoundingClientRect().top;

      let currentId = '';
      let minDistance = Infinity;

      sectionIds.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top - containerTop;
          const distance = Math.abs(elementTop - 100); // 100px offset from top

          if (elementTop <= 150 && distance < minDistance) {
            minDistance = distance;
            currentId = id;
          }
        }
      });

      if (currentId && currentId !== activeId) {
        setActiveId(currentId);
      }
    };

    // IntersectionObserver setup
    const observerOptions = {
      root: contentScrollRef.current,
      rootMargin: '-100px 0px -50% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1],
    };

    const observerCallback = (entries) => {
      let topMostEntry = null;
      let topMostPosition = Infinity;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const rect = entry.boundingClientRect;
          if (rect.top < topMostPosition) {
            topMostPosition = rect.top;
            topMostEntry = entry;
          }
        }
      });

      if (topMostEntry) {
        setActiveId(topMostEntry.target.id);
      }
    };

    observerRef.current = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe all sections
    setTimeout(() => {
      sectionIds.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          observerRef.current.observe(element);
        }
      });

      // Initial check
      handleScroll();
    }, 100);

    // Add scroll listener as backup
    const scrollContainer = contentScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeLeft, tocData, showTitle]);

  const breadcrumbData = {
    name: articleTitles[activeLeft] || articleTitles[0],
    item: [
      { name: 'SG Help Center', path: -2 },
      { name: sectionName, path: -1 },
    ],
  };

  // Get all searchable content
  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];

    const results = [];
    const query = searchQuery.toLowerCase();

    articleTitles.forEach((title, index) => {
      if (title.toLowerCase().includes(query)) {
        results.push({
          type: 'article',
          title: title,
          articleIndex: index,
        });
      }

      const sections = tocData[index] || [];
      sections.forEach((section) => {
        if (section.toLowerCase().includes(query)) {
          results.push({
            type: 'section',
            title: section,
            articleTitle: title,
            articleIndex: index,
            sectionId: generateSectionId(section),
          });
        }
      });
    });

    return results;
  };

  const searchResults = getSearchResults();

  const handleSearchResultClick = (result) => {
    setSearchQuery('');
    setShowSearchResults(false);

    if (result.type === 'article') {
      setActiveLeft(result.articleIndex);
      // CHANGED: Scroll only content container to top
      setTimeout(() => {
        if (contentScrollRef.current) {
          contentScrollRef.current.scrollTop = 0;
        }
      }, 0);
    } else if (result.type === 'section') {
      setActiveLeft(result.articleIndex);
      // CHANGED: Scroll within content container
      setTimeout(() => {
        const element = document.getElementById(result.sectionId);
        if (element && contentScrollRef.current) {
          const containerRect =
            contentScrollRef.current.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const scrollTop = contentScrollRef.current.scrollTop;
          const targetScroll =
            scrollTop + (elementRect.top - containerRect.top) - 20; // 20px offset from top

          contentScrollRef.current.scrollTo({
            top: targetScroll,
            behavior: 'smooth',
          });
        }
      }, 300);
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 pt-32 md:pt-40 text-white">
      <div className="max-w-screen-xl mx-auto flex flex-col items-start gap-6">
        {/* Header Section */}
        <div className="flex flex-col w-full gap-4">
          {/* Breadcrumb and Menu Button Row */}
          <div className="flex justify-between items-center w-full gap-4">
            <Brodcrumb data={breadcrumbData} />

            {/* Search Bar - Desktop */}
            <div className="hidden lg:block relative w-[350px]" ref={searchRef}>
              <Input
                leftIcon={
                  <img
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 h-[18px]"
                    src={imagePath?.SearchIcon}
                    alt="Search"
                  />
                }
                className="w-full !h-[40px] !rounded-[40px] bg-[#31333A] pl-12 text-white placeholder:text-[#B8B8B8] outline-none"
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => searchQuery && setShowSearchResults(true)}
              />

              {/* Search Results Dropdown - Desktop */}
              {showSearchResults && searchQuery.trim() && (
                <SearchResultsDropdown
                  searchResults={searchResults}
                  onResultClick={handleSearchResultClick}
                />
              )}
            </div>

            {/* Menu Button for Mobile/Tablet */}
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden text-white focus:outline-none"
              aria-label="Toggle Menu"
            >
              <img
                src={iconsPath.menuIcon}
                alt="menu icon"
                className="h-[26px] w-[26px] cursor-pointer"
              />
            </button>
          </div>

          {/* Search and On This Page Row - Mobile/Tablet Only */}
          <div className="flex lg:hidden gap-3 w-full items-center">
            {/* Search Bar - Mobile/Tablet */}
            <div className="relative w-[200px] sm:w-[240px]" ref={searchRef}>
              <Input
                leftIcon={
                  <img
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 h-[18px]"
                    src={imagePath?.SearchIcon}
                    alt="Search"
                  />
                }
                className="w-full !h-[40px] !rounded-[40px] bg-[#31333A] pl-12 text-white placeholder:text-[#B8B8B8] outline-none"
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                  setOpenToc(false);
                }}
                onFocus={() => {
                  if (searchQuery) setShowSearchResults(true);
                  setOpenToc(false);
                }}
              />

              {/* Search Results Dropdown - Mobile */}
              {showSearchResults && searchQuery.trim() && (
                <SearchResultsDropdown
                  searchResults={searchResults}
                  onResultClick={handleSearchResultClick}
                />
              )}
            </div>

            {/* On This Page Button for Mobile/Tablet */}
            <div className="relative w-[200px] sm:w-[240px]" ref={tocRef}>
              <button
                onClick={() => {
                  setOpenToc(!openToc);
                  setShowSearchResults(false);
                }}
                className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-[#31333A] rounded-[40px] text-sm font-medium hover:bg-[#3a3c43] transition-colors h-[40px]"
              >
                <span>On this page</span>
                <svg
                  className={`w-4 h-4 transition-transform ${openToc ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {openToc && (
                <div className="absolute left-0 top-full mt-2 w-full bg-[#31333A] rounded-lg border border-[#444447] shadow-xl z-50 max-h-[60vh] overflow-y-auto scrollbar-thin">
                  <div className="p-3">
                    <RightSidebar
                      tocData={tocData}
                      activeLeft={activeLeft}
                      activeId={activeId}
                      onItemClick={() => setOpenToc(false)}
                      isMobile={true}
                      contentScrollRef={contentScrollRef}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full flex flex-col lg:flex-row justify-start items-start gap-8 mt-4 h-[calc(100vh-160px)]">
          {/* Left Sidebar */}
          <div className="hidden lg:flex flex-[3]">
            <LeftSidebar
              articleTitles={articleTitles}
              activeLeft={activeLeft}
              setActiveLeft={setActiveLeft}
            />
          </div>

          {/* Center Content - CHANGED: Added ref to scrollable container */}
          <div
            ref={contentScrollRef}
            className="flex-[10] w-full h-full overflow-y-auto pr-2 scrollbar"
          >
            {showTitle && <ContentHeader title={articleTitles[activeLeft]} />}
            <ContentBodyComponent activeLeft={activeLeft} />

            {/* Use Cases Section */}
            {showUseCases && (
              <Section title="Use Cases">
                <div className="w-full flex justify-start">
                  <div className="flex flex-col gap-3">
                    {helpCenterUseCases.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => navigate(item.path)}
                        className="text-sm md:text-base text-[#519FFF] underline cursor-pointer hover:text-white font-light transition-colors"
                      >
                        {item.title}
                      </div>
                    ))}
                  </div>
                </div>
              </Section>
            )}
          </div>

          {/* Right Sidebar - CHANGED: Pass contentScrollRef */}
          <div className="hidden lg:flex flex-[3]">
            <RightSidebar
              tocData={tocData}
              activeLeft={activeLeft}
              activeId={activeId}
              contentScrollRef={contentScrollRef}
            />
          </div>
        </div>

        {/* Drawer for Mobile View */}
        <Drawer zInd={10000} isOpen={open} setIsOpen={setOpen}>
          <LeftSidebar
            articleTitles={articleTitles}
            activeLeft={activeLeft}
            setActiveLeft={setActiveLeft}
            onClose={() => setOpen(false)}
          />
        </Drawer>
      </div>
    </div>
  );
};

/* ----------------------------------------
   Search Results Dropdown
----------------------------------------- */
const SearchResultsDropdown = ({ searchResults, onResultClick }) => {
  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-[#31333A] border border-[#5c5c5c] rounded-lg shadow-lg max-h-[400px] overflow-y-auto z-50 scrollbar">
      {searchResults.length > 0 ? (
        <div className="py-2">
          {searchResults.map((result, index) => (
            <div
              key={index}
              onMouseDown={(e) => {
                e.preventDefault();
                onResultClick(result);
              }}
              onClick={() => onResultClick(result)}
              className="px-4 py-3 hover:bg-[#3a3c43] cursor-pointer transition-colors border-b border-[#444447] last:border-b-0"
            >
              <div className="flex flex-col gap-1">
                <div className="text-sm font-medium text-white">
                  {result.title}
                </div>
                {result.type === 'section' && (
                  <div className="text-xs text-gray-400">
                    in {result.articleTitle}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-4 py-3 text-sm text-gray-400">No results found</div>
      )}
    </div>
  );
};

/* ----------------------------------------
   Left Sidebar
----------------------------------------- */
const LeftSidebar = React.memo(
  ({ articleTitles, activeLeft, setActiveLeft, onClose }) => {
    return (
      <div className="flex flex-col gap-5">
        <div className="pl-4 lg:pl-0 text-sm font-medium">
          Articles in this section
        </div>
        <div className="flex flex-col gap-1">
          {articleTitles.map((title, i) => (
            <div
              key={i}
              className={`text-[13px] cursor-pointer px-3 py-2.5 w-full rounded-[5px] ${
                activeLeft === i ? 'bg-[#31333A]' : 'hover:bg-[#2a2b30]'
              }`}
              onClick={() => {
                if (onClose) onClose();
                setActiveLeft(i);
              }}
            >
              {title}
            </div>
          ))}
        </div>
      </div>
    );
  }
);

/* ----------------------------------------
   Content Header
----------------------------------------- */
const ContentHeader = ({ title }) => {
  return <div className="text-2xl md:text-3xl font-medium mb-6">{title}</div>;
};

/* ----------------------------------------
   Right Sidebar - CHANGED: Added contentScrollRef prop
----------------------------------------- */
const RightSidebar = React.memo(
  ({
    tocData,
    activeLeft = 0,
    activeId = '',
    onItemClick,
    isMobile = false,
    contentScrollRef,
  }) => {
    const topics = tocData[activeLeft] || tocData[0] || [];

    const handleClick = (title) => {
      const id = generateSectionId(title);
      const element = document.getElementById(id);

      // CHANGED: Scroll within content container instead of entire page
      if (element && contentScrollRef?.current) {
        const containerRect = contentScrollRef.current.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const scrollTop = contentScrollRef.current.scrollTop;
        const targetScroll =
          scrollTop + (elementRect.top - containerRect.top) - 20; // 20px offset from top

        contentScrollRef.current.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
        });
      }

      if (onItemClick) onItemClick();
    };

    return (
      <div
        className={`flex flex-col gap-5 ${!isMobile ? 'border-l-2 border-[#444447] pl-4' : ''}`}
      >
        <div className="text-sm font-medium">On this page</div>
        <div className="flex flex-col gap-1">
          {topics.map((title, i) => {
            const id = generateSectionId(title);
            const isActive = activeId === id;

            return (
              <div
                key={i}
                onClick={() => handleClick(title)}
                className={`text-[13px] font-light cursor-pointer py-2 transition-colors ${
                  isActive ? 'text-[#EE2B2A] font-medium' : ''
                }`}
              >
                {title}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

/* ----------------------------------------
   Section Component
----------------------------------------- */
export const Section = ({ title, children }) => {
  return (
    <div className="w-full my-10 flex flex-col items-start">
      {title && (
        <h2 className="text-[18px] md:text-[20px] lg:text-[22px] text-[#D8D8D8] font-medium mb-6">
          {title}
        </h2>
      )}
      {children}
    </div>
  );
};

export default ArticleLayout;
